import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '@strapi/design-system';

import useBreadcrumbsStorage from '#shared/hooks/useBreadcrumbsStorage';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getPluginName, extractPluginPath } from '#shared/utils/pluginUrl';

import useURL from '../../hooks/useURL';

import Crumb from './components/Crumb';
import {
  ITEM_SESSION_STORAGE_BLOCK_NAVIGATION,
  BLOCK_NAVIGATION_TRUE,
  CUSTOM_EVENT_SLICE_CRUMB,
  MAX_NODES_IN_BREADCRUMB,
} from './utils/constants';

import {
  PLUGIN_TITLE_COURSES,
  PLUGIN_TITLE_ACTIVITIES,
  PLUGIN_TITLE_GLOSSARY,
  PLUGIN_TITLE_FLASHCARDS,
  PLUGIN_TITLE_CRITICAL_CONCEPTS,
  PLUGIN_TITLE_LEARNING_OBJECTIVES,
  SECTION_TITLE_CREATE_NEW_COURSE,
  SECTION_TITLE_IMPORT_COURSE,
} from '#shared/constants/common';

const CONTENT_LIBRARY_SEARCH_BREADCRUMB_TITLE = 'Content Library Search';

export default function Breadcrumb({ title, isDraft, initialCrumbs }) {
  const { history, currentQueryParams } = useURL();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [crumbsState, setCrumbsState] = useState([]); // Only to trigger re-renders.

  const { getCrumbs, saveCrumbs: saveCrumbsInStorage } =
    useBreadcrumbsStorage();

  useEffect(() => {
    updateLastCrumb();
  }, [history?.location.search]);

  const flags = useFlags();
  const { showBreadcrumbs } = flags;

  const updateLastCrumb = () => {
    let crumbs = getCrumbs();

    if (!crumbs[crumbs?.length - 1]?.url) return null;
    const cleanUrl = crumbs[crumbs?.length - 1]?.url.match(/^[^?]+/)[0];

    const completeCurrentURL =
      history?.location.pathname + history?.location.search;

    if (
      crumbs[crumbs?.length - 1]?.url &&
      crumbs[crumbs?.length - 1]?.url !== completeCurrentURL &&
      history?.location.pathname === cleanUrl
    ) {
      crumbs[crumbs.length - 1].url = completeCurrentURL;
    }

    saveCrumbs(crumbs);
  };

  const addCrumb = () => {
    let tempCrumbs = getCrumbs();

    const newElement = {
      label: title,
      url: history?.location.pathname + history?.location.search,
    };

    if (newElement.url !== tempCrumbs[tempCrumbs?.length - 1]?.url) {
      tempCrumbs.push(newElement);
      saveCrumbs(tempCrumbs);
    }
  };

  const checkMaxNodesInBreadcrumb = (breadcrumb) =>
    breadcrumb.length > MAX_NODES_IN_BREADCRUMB;

  const saveCrumbs = (crumbs) => {
    saveCrumbsInStorage(crumbs);
    setCrumbsState(crumbs);
  };

  const initialCrumbsKey = JSON.stringify(initialCrumbs ?? []);

  useEffect(() => {
    if (!initialCrumbs?.length) return;

    saveCrumbs(initialCrumbs);
  }, [history?.location.pathname, initialCrumbsKey]);

  const sliceCrumb = async (index) => {
    const crumbs = getCrumbs();
    let tempCrumbs = crumbs?.slice(0, index + 1);
    saveCrumbs(tempCrumbs);
  };

  const handler = async (index) => {
    const blockNavigation = sessionStorage.getItem(
      ITEM_SESSION_STORAGE_BLOCK_NAVIGATION,
    );

    if (blockNavigation !== BLOCK_NAVIGATION_TRUE) {
      await sliceCrumb(index);
    }

    if (blockNavigation === BLOCK_NAVIGATION_TRUE) {
      const handleSliceCrumb = async () => {
        await sliceCrumb(index);

        removeEventListener(CUSTOM_EVENT_SLICE_CRUMB, handleSliceCrumb);
      };

      addEventListener(CUSTOM_EVENT_SLICE_CRUMB, handleSliceCrumb);
    }

    window.location.href = crumbsState[index].url;
  };

  useEffect(() => {
    if (title && !isDraft) addCrumb();
  }, [title, currentQueryParams, history?.location.pathname, initialCrumbsKey]);

  const removeCrumbFromStorage = () => {
    const crumbs = getCrumbs();
    const index = crumbs.findIndex(
      (crumb) =>
        crumb?.label === SECTION_TITLE_CREATE_NEW_COURSE ||
        crumb?.label === SECTION_TITLE_IMPORT_COURSE
    );

    if (index !== -1 && index !== crumbs.length - 1) {
      const newCrumbs = [...crumbs];
      newCrumbs.splice(index, 1);
      saveCrumbs(newCrumbs);
    }
  };

  useEffect(() => {
    const crumbs = getCrumbs();
    const pathname = history?.location.pathname;
    const completeCurrentURL =
      history?.location.pathname + history?.location.search;
    const pluginName = getPluginName(pathname);
    const pluginRoot = {
      label: pluginName,
      url: extractPluginPath(pathname),
    };
    const hasInitialCrumbs = Boolean(initialCrumbs?.length);

    if (
      !hasInitialCrumbs &&
      crumbs?.[0]?.label === CONTENT_LIBRARY_SEARCH_BREADCRUMB_TITLE
    ) {
      const currentCrumb =
        crumbs[crumbs.length - 1]?.url === completeCurrentURL
          ? crumbs[crumbs.length - 1]
          : null;

      saveCrumbs(
        currentCrumb && currentCrumb.url !== pluginRoot.url
          ? [pluginRoot, currentCrumb]
          : [pluginRoot],
      );
      return;
    }

    if (
      crumbs?.[0]?.label !== PLUGIN_TITLE_COURSES &&
      crumbs?.[0]?.label !== PLUGIN_TITLE_ACTIVITIES &&
      crumbs?.[0]?.label !== PLUGIN_TITLE_GLOSSARY &&
      crumbs?.[0]?.label !== PLUGIN_TITLE_FLASHCARDS &&
      crumbs?.[0]?.label !== PLUGIN_TITLE_CRITICAL_CONCEPTS &&
      crumbs?.[0]?.label !== PLUGIN_TITLE_LEARNING_OBJECTIVES &&
      crumbs?.[0]?.label !== CONTENT_LIBRARY_SEARCH_BREADCRUMB_TITLE
    ) {
      saveCrumbs([pluginRoot, ...crumbs]);
    }
    removeCrumbFromStorage();
  }, []);

  useEffect(() => {
    setIsCollapsed(checkMaxNodesInBreadcrumb(getCrumbs()));
  }, [getCrumbs().length]);

  if (getCrumbs().length < 1) return '';

  return (
    showBreadcrumbs && (
      <div>
        <Breadcrumbs label="Breadcrumb">
          {isCollapsed ? (
            <>
              <Crumb
                key="0"
                handler={() => handler(0)}
                label={getCrumbs()[0].label}
              />
              <Crumb
                key="1"
                handler={() => setIsCollapsed(false)}
                label="..."
              />
              <Crumb
                key={getCrumbs().length - 1}
                handler={() => handler(getCrumbs().length - 2)}
                label={getCrumbs()[getCrumbs().length - 2]?.label}
              />
              <Crumb
                key={getCrumbs().length}
                handler={() => handler(getCrumbs().length - 1)}
                label={getCrumbs()[getCrumbs().length - 1]?.label}
                isCurrent={true}
              />
            </>
          ) : (
            getCrumbs()?.map((crumb, index) => (
              <Crumb
                key={index}
                handler={() => handler(index)}
                label={crumb.label}
                isCurrent={getCrumbs().length - 1 === index}
              />
            ))
          )}
        </Breadcrumbs>
      </div>
    )
  );
}
